import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Counter, Img, Parallax, Reveal } from "@/components/site/Motion";
import { Section } from "@/components/site/Section";
import { Gallery } from "@/components/site/Gallery";
import { ReviewsCarousel } from "@/components/site/Reviews";
import { ProfileCards } from "@/components/site/Modals";
import { ContactCTA, Steps, useSeo } from "@/components/site/PageParts";
import { ProcoreBadge } from "@/components/site/Procore";
import { AboutMore } from "@/components/site/AboutModal";
import { ProjectSlider } from "@/components/site/Projects";
import { company, productRange, profiles, residentialSystems, commercialSystems, commercialProjects, commercialFilters, residentialProjects, residentialFilters, verifiedWork as W } from "@/content/site";

const MapleLeaf = () => (
  <svg viewBox="0 0 512 512" className="ml-1 h-[30px] w-[30px] self-end text-[#E0312B]" role="img" aria-label="Canada" data-testid="hero-canada-symbol">
    <path fill="currentColor" d="M256 16l-38 72c-4 8-12 7-20 3l-28-14 21 110c4 20-10 20-17 11l-48-54-8 27c-1 4-5 7-10 6l-60-13 16 58c3 13 6 18-4 22l-21 10 104 85c4 3 6 9 5 14l-9 30c36-4 69-11 105-15 3 0 8 4 8 8l-5 112h18l-5-112c0-4 5-8 8-8 36 4 69 11 105 15l-9-30c-1-5 1-11 5-14l104-85-21-10c-10-4-7-9-4-22l16-58-60 13c-5 1-9-2-10-6l-8-27-48 54c-7 9-21 9-17-11l21-110-28 14c-8 4-16 5-20-3z" />
  </svg>
);

const Indicators = ({ className = "" }) => (
  <div className={`indicators ${className}`} data-testid="hero-indicators">
    <div data-testid="hero-stat-established">
      <p className="text-xl font-bold leading-none text-white md:text-2xl">{company.established}</p>
      <p className="mt-1.5 text-xs text-[#D6E0E9]">Established</p>
    </div>
    <span className="w-px self-stretch bg-white/25" aria-hidden="true" />
    <div data-testid="hero-stat-experience">
      <p className="text-xl font-bold leading-none text-white md:text-2xl"><Counter value={company.experienceYears} /></p>
      <p className="mt-1.5 max-w-[4.6rem] text-xs leading-tight text-[#D6E0E9] sm:max-w-none">years of experience</p>
    </div>
    <MapleLeaf />
  </div>
);

const Hero = () => (
  <section className="relative" aria-labelledby="home-title" data-testid="home-hero">
    <div className="hero-box relative mx-auto flex w-full max-w-[1440px] flex-col px-5 pb-0 pt-[calc(68px+4vh)] md:px-8 md:pt-[calc(76px+5vh)] wide:pb-8 wide:pt-[calc(76px+7vh)]">
      <div className="max-w-xl">
        <Reveal>
          <h1 id="home-title" className="text-[2.15rem] font-bold leading-[1.06] text-white sm:text-5xl lg:text-[3.1rem] xl:text-[3.6rem]">
            Architectural aluminum windows, doors &amp; facades.
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-[#EEF2F5] md:text-lg" data-testid="hero-sentence">
            Design, manufacture, glazing and installation, all from one team since 2001.
          </p>
          <p className="mt-3 max-w-lg text-lg font-bold leading-snug text-white md:text-xl" data-testid="hero-strong-line">
            From high-rise, government and commercial projects to cottages and family homes across Canada.
          </p>
        </Reveal>
      </div>
      <div className="mt-auto pt-8">
        <div className="rise-in mb-4 flex max-w-md items-center justify-between gap-3">
          <Indicators className="flex" />
          <img src="/img/badge-25-240.webp" srcSet="/img/badge-25-120.webp 175w, /img/badge-25-240.webp 350w" sizes="120px" width="350" height="240" alt={`${company.experienceYears} years anniversary`} className="anniv-badge h-[60px] w-auto flex-none md:h-[76px]" data-testid="hero-anniversary-badge" />
        </div>
        <div className="rise-in rise-delay grid max-w-md grid-cols-2 gap-3 [&>a]:justify-center">
          <Link to="/residential" className="btn-glass btn-on-dark btn-lg" data-testid="hero-residential-btn">Residential <ArrowRight aria-hidden="true" size={18} /></Link>
          <Link to="/commercial" className="btn-glass btn-on-dark btn-lg" data-testid="hero-commercial-btn">Commercial <ArrowRight aria-hidden="true" size={18} /></Link>
        </div>
      </div>
    </div>
  </section>
);

// Short company intro near the top; the full About section sits at the bottom of the page.
const AboutSummary = () => (
  <Section id="about-summary" tone="pale" eyebrow="About Alumicom" title="One company from design to installation.">
    <Reveal className="grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-center lg:gap-16">
      <p className="text-base leading-relaxed text-[#151A20] md:text-lg" data-testid="about-summary-text">Since {company.established}, Alumicom has designed, manufactured, glazed and installed aluminum systems for high-rise, commercial and residential projects across {company.serviceArea}, with its own shop in Vaughan.</p>
      <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-[14px] border border-[#D9E1E8] bg-[#D9E1E8]">
        {[[company.experienceYears, "Years"], [residentialSystems.length + commercialSystems.length, "Systems"], [company.googleRating, "Google rating"]].map(([v, l]) => (
          <div key={l} className="bg-white p-4"><dt className="text-xs text-[#3E5468]">{l}</dt><dd className="mt-1 text-2xl font-bold tabular-nums text-[#102A43] md:text-3xl">{v}</dd></div>
        ))}
      </dl>
    </Reveal>
    <AboutMore label="Learn more" testid="about-summary-btn" className="mt-8" />
  </Section>
);

const About = () => (
  <Section id="about" tone="pale" eyebrow="About Alumicom" title="One company from design to installation." lead="A leader in aluminum systems fabrication and installation since 2001. Today Alumicom is a full-service design, manufacture and glazing operation serving high-rise, commercial and residential projects across Canada.">
    <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-20">
      <Reveal className="grid gap-6 text-base leading-relaxed text-[#151A20] md:text-lg">
        <p>We take pride in our commitment to excellence and innovation. Whether you are planning sleek, modern facades for a high-rise or durable, energy-efficient solutions for a residential development, we have the expertise and resources to bring your vision to life.</p>
        <p className="font-semibold text-[#102A43]">What sets us apart is our dedication to quality craftsmanship, attention to detail and customer satisfaction.</p>
        <p>Our experienced team gives personalized service and support at every stage of your project, from initial concept to final installation. We talk each project through openly, our own shop fabricates the system and our crews install it.</p>
        <p>Alumicom invests in research and development and works with long-term suppliers and industry partners, so clients can use current products and materials.</p>
      </Reveal>
      <Reveal delay={0.08}>
        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-[14px] border border-[#D9E1E8] bg-[#D9E1E8]" data-testid="about-metrics">
          {[[company.established, "Established"], [company.experienceYears, "Years of experience"], [residentialSystems.length + commercialSystems.length, "System families"], [company.googleRating, "Google rating"]].map(([v, l]) => (
            <div key={l} className="bg-white p-5 md:p-6"><dt className="text-sm text-[#3E5468]">{l}</dt><dd className="mt-2 text-3xl font-bold tabular-nums text-[#102A43] md:text-4xl">{v}</dd></div>
          ))}
        </dl>
        <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-6 text-sm">
          <div><dt className="text-[#3E5468]">Clients</dt><dd className="mt-1 text-base font-semibold">Homeowners, architects, builders, developers</dd></div>
          <div><dt className="text-[#3E5468]">Facility</dt><dd className="mt-1 text-base font-semibold">Vaughan, Ontario</dd></div>
          <div><dt className="text-[#3E5468]">Services</dt><dd className="mt-1 text-base font-semibold">Design, manufacture, glazing, installation</dd></div>
          <div><dt className="text-[#3E5468]">Markets</dt><dd className="mt-1 text-base font-semibold">High-rise, commercial, residential</dd></div>
          <div><dt className="text-[#3E5468]">Service area</dt><dd className="mt-1 text-base font-semibold">{company.serviceArea}</dd></div>
        </dl>
      </Reveal>
    </div>
    <Reveal className="mt-12 border-t border-[#C9D3DC] pt-8">
      <h3 className="text-lg font-bold text-[#102A43] md:text-xl">Our range of aluminum systems</h3>
      <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-[#2B3642]">Tailored to the unique requirements of each project.</p>
      <ol className="mt-6 grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-product-range">
        {productRange.map((p, i) => (
          <li key={p} className="flex items-baseline gap-3 border-b border-[#D9E1E8] pb-3 text-[15px] font-semibold text-[#151A20]">
            <span className="w-6 flex-none text-sm tabular-nums text-[#7890A5]">{String(i + 1).padStart(2, "0")}</span>{p}
          </li>
        ))}
      </ol>
    </Reveal>
    <AboutMore />
  </Section>
);

// One self-contained card per market: cover + summary on top, its project gallery inside the same card.
const Solution = ({ to, title, text, items, image, imgPos = "50% 50%", testid, slider }) => (
  <Reveal>
    <article className="card overflow-hidden" data-testid={`${testid}-card`}>
      <Link to={to} className="group grid focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#102A43] wide:grid-cols-[1.15fr_1fr]" data-testid={testid}>
        <div className="relative aspect-[16/10] overflow-hidden wide:aspect-auto wide:min-h-[340px]">
          <Img {...image} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" style={{ objectPosition: imgPos }} />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#102A43]/90 via-[#102A43]/40 to-transparent p-6 pt-24 md:p-8">
            <h3 className="text-2xl font-bold text-white md:text-3xl">{title}</h3>
          </div>
        </div>
        <div className="flex flex-col justify-center p-6 md:p-8 lg:p-10">
          <p className="text-base leading-relaxed text-[#151A20] md:text-lg">{text}</p>
          <p className="mt-4 text-sm text-[#2B3642]">{items}</p>
          <span className="mt-6 inline-flex items-center gap-2 font-semibold text-[#102A43]">View {title.toLowerCase()} systems <ArrowRight aria-hidden="true" size={16} className="transition-transform group-hover:translate-x-1" /></span>
        </div>
      </Link>
      <div className="border-t border-[#D9E1E8] px-5 pb-6 pt-5 md:px-8 md:pb-8 lg:px-10">{slider}</div>
    </article>
  </Reveal>
);

const Solutions = () => (
  <Section id="solutions" transparent eyebrow="Solutions" title="Residential and commercial systems." lead="Choose the side that matches your project.">
    <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:gap-14">
      <Solution to="/residential" testid="solutions-residential-link" title="Residential" image={{ name: "rp-01", w: 1076, h: 807, alt: "Lakeside home with large black-framed aluminum windows and a covered terrace" }} text="Windows, entry doors, lift-slide and folding doors, Passive House systems, facades and skylights for new homes and renovations." items="MB-79N · MB-77HS · MB-86 Foldline · MB-104 · MB-TT50"
        slider={<ProjectSlider items={residentialProjects} filters={residentialFilters} label="Residential projects" tone="light" testid="home-residential-slider" className="" />} />
      <Solution to="/commercial" testid="solutions-commercial-link" title="Commercial" imgPos="50% 0%" image={{ name: "cp-01", w: 1448, h: 1086, alt: "Commercial building entrance with a two-storey aluminum curtain wall and glass storefront" }} text="Curtain wall, window wall, commercial doors, balcony railings, skylights and cladding for commercial, multi-unit and high-rise buildings." items="2000/2500 curtain wall · 127 window wall · 45/53/450 doors · 36/38 railing · AC3/AC4 cladding"
        slider={<ProjectSlider items={commercialProjects} filters={commercialFilters} label="Commercial projects" tone="light" testid="home-commercial-slider" className="" />} />
    </div>
    <ProcoreBadge className="mt-10 lg:mt-14" compact />
  </Section>
);

// Small product spotlight: the owner's clip, looped as-is (no added effects).
const Spotlight = () => (
  <section id="featured" className="section-gap" aria-labelledby="spotlight-title" data-testid="section-spotlight">
    <div className="container-x">
      <Reveal>
        <article className="card mx-auto grid max-w-[340px] gap-4 overflow-hidden rounded-[22px] p-5 wide:max-w-[660px] wide:grid-cols-[260px_1fr] wide:gap-x-7 wide:gap-y-3 wide:p-6" data-testid="spotlight-card">
          <div className="wide:col-start-2 wide:row-start-1 wide:self-end">
            <h2 id="spotlight-title" className="text-xl font-bold uppercase leading-tight tracking-[0.02em] text-[#102A43]">Curtain wall system</h2>
            <p className="mt-0.5 text-sm font-semibold text-[#3E5468]" data-testid="spotlight-series">2000 and 2500 Series</p>
          </div>
          <video className="spot-video mx-auto block aspect-[900/942] w-full max-w-[200px] wide:col-start-1 wide:row-span-2 wide:row-start-1 wide:max-w-[260px] wide:self-center" poster="/img/cw-loop-v3-poster.webp" autoPlay muted loop playsInline preload="auto" aria-label="Rotating cut-away of the 2000 and 2500 Series curtain wall profile with triple glazing" data-testid="spotlight-video">
            <source src="/img/cw-loop-v3.mp4" type="video/mp4" />
            <source src="/img/cw-loop-v3.webm" type="video/webm" />
          </video>
          <div className="wide:col-start-2 wide:row-start-2 wide:self-start">
            <p className="text-sm leading-relaxed text-[#2B3642]" data-testid="spotlight-caption">A closer look at one of our commercial aluminum and glass solutions. Explore our commercial systems to discover more options.</p>
            <Link to="/commercial#solutions" className="btn-glass mt-4" data-testid="spotlight-more-btn">See more systems <ArrowRight aria-hidden="true" size={16} /></Link>
          </div>
        </article>
      </Reveal>
    </div>
  </section>
);

const Work = () => (
  <Section id="projects" eyebrow="Our work" title="On site and in the shop." lead="Alumicom crews delivering, installing and glazing on residential and commercial sites, and fabrication in our Vaughan facility." action={{ label: "Request a project quote" }}>
    <Gallery items={[W.midRise, W.lowRise, W.townhouses, W.delivery, W.dispatch, W.shopFloor]} testid="home-gallery" />
  </Section>
);

const PROCESS = [
  ["Enquiry", "Send the form or call. Tell us the project type, location and what you have so far."],
  ["Consultation", "We discuss your requirements and help you choose the configuration and materials."],
  ["Quotation", "You receive a proposal based on your drawings, specifications and selections."],
  ["Design", "Systems are detailed to suit the project specification."],
  ["Fabrication", "Frames and components are made in Alumicom's own shop."],
  ["Installation and glazing", "Our team installs and glazes the system on site."],
];

const STRENGTHS = [
  ["Design, fabrication and glazing in one company", "One team is responsible from the first drawing to the installed system."],
  ["Our own shop", "Profiles are cut, machined, assembled and glazed in Alumicom's Vaughan facility."],
  ["Documented systems", "Every product family has published specifications, from MB-79N windows to the 127 Series window wall."],
  ["Quality assurance", "A quality assurance process checks workmanship and materials before systems leave the shop."],
];

const Strengths = () => (
  <Section id="strengths" eyebrow="Why Alumicom" title="Capability you can see in the shop and on site.">
    <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
      <div className="grid content-start gap-10 lg:col-span-6">
      <ul className="grid content-start gap-8">
        {STRENGTHS.map(([t, d], i) => (
          <li key={t}><Reveal delay={i * 0.05} className="border-l-2 border-[#102A43] pl-6">
            <h3 className="text-lg font-semibold text-[#102A43]">{t}</h3>
            <p className="mt-2 text-base leading-relaxed text-[#151A20]">{d}</p>
          </Reveal></li>
        ))}
      </ul>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:col-span-6">
        <Parallax className="img-frame aspect-[4/3] sm:col-span-2 sm:aspect-[16/10]"><Img {...W.assembly} className="h-full w-full object-cover" /></Parallax>
        <div className="img-frame aspect-[4/3]"><Img {...W.design} sizes="(min-width:1024px) 25vw, 50vw" className="h-full w-full object-cover" /></div>
        <p className="self-end text-sm leading-relaxed text-[#2B3642]">Detailing at the drafting desk and frame assembly in the Alumicom shop.</p>
      </div>
    </div>
  </Section>
);

export default function Home() {
  useSeo("ALUMICOM | Architectural Aluminum Windows, Doors & Facades | Vaughan, Ontario", "Alumicom designs, manufactures and installs aluminum windows, doors, curtain wall, window wall, railings and cladding for residential, commercial and high-rise projects. Established 2001.");
  return (
    <>
      <Hero />
      <Section id="are-you" tight eyebrow="Find your starting point" title="Are you…">
        <ProfileCards profiles={profiles} />
      </Section>
      <AboutSummary />
      <Spotlight />
      <Solutions />
      <Work />
      <Section id="process" tone="navy" eyebrow="How it works" title="From enquiry to installation." lead="The same sequence applies to every project. Scope and timing are confirmed with each quotation.">
        <Steps steps={PROCESS} />
      </Section>
      <Strengths />
      <Section id="reviews" variant="glass" tone="pale" eyebrow="Client reviews" title="What clients say.">
        <ReviewsCarousel />
      </Section>
      <About />
      <ContactCTA title="Tell us about your project." lead="An Alumicom agent will review your enquiry and contact you by your preferred method." image={{ name: "ww-span", w: 1200, h: 900 }} action={{ label: "Discuss your project" }} />
    </>
  );
}
